
import { IImageStorage, utils } from 'oneplace-components';
import { IApi } from '../../../data_sources/api';
import { IIncidentV2 } from '../../../models/Incident';
import {
    addChecklistPhotosToFormData,
    collateChecklistPhotos,
    collateNewChecklistPhotoComments,
    fixChecklistData
} from '../../checklists/utils/submitChecklist';

/*
 * Mirrors submitChecklist() in this folder against the anonymous incident survey endpoint. The
 * checklist photo-collation helpers are reused as-is: they're generic over the shared
 * groups/fields/photos/dataGrid shape, and IIncidentV2 is structurally an IChecklist.
 */

export interface IIncidentReportResponse {
    incidentNo: number;
}

/*
 * The register lists the incident's own severity/injuryType/name/date columns, which the backend
 * only writes from these top-level properties - never from the answers. Mirrors CMA's method of the
 * same name so both clients behave alike. CMA's `hazard` case is omitted: the hazard question isn't
 * served on the survey, so there's never an answer to map.
 */
export function incidentDefaultFieldResponsesToRoot(incident: IIncidentV2) {
    const detailsGroup = incident.groups && incident.groups[0];
    if (!detailsGroup || !detailsGroup.fields) {
        return;
    }

    detailsGroup.fields.forEach((field) => {
        if (!field.mappingField?.value) return;

        switch (field.mappingField.value) {
            case 'personName':
                incident.name = field.value;
                break;
            case 'dateOf':
                incident.date = field.value;
                break;
            case 'severity':
                incident.severity = field.value;
                break;
            case 'injuryType':
                incident.injuryType = field.value;
                break;
        }
    });
}

export async function submitIncidentReport(
    api: IApi,
    incident: IIncidentV2,
    imageStorage: IImageStorage,
    emails?: string,
    includePdf: boolean = false,
    timeout: number = 300000
) {
    const incidentPostData = new FormData();

    const incidentData = utils.deepCopy(incident);

    // at submit time, as CMA does it
    incidentDefaultFieldResponsesToRoot(incidentData);

    // Collate photos
    const photoList = collateChecklistPhotos(incidentData);

    // Collate new photo comments
    collateNewChecklistPhotoComments(photoList, incidentData);

    // Prepare incident data for upload
    fixChecklistData(incidentData);

    incidentPostData.append('incident', JSON.stringify({ incident: incidentData }));

    // part of this same request rather than a second, addressable call - the backend sends the
    // copy off a queue once the incident exists
    if (emails && emails.trim() !== '') {
        incidentPostData.append('copyToEmail', emails.trim());
    }

    // only meaningful if the survey allows it - the backend re-checks regardless of what's sent
    incidentPostData.append('includePdf', String(includePdf));

    await addChecklistPhotosToFormData(imageStorage, photoList, incidentPostData);

    return api.submitIncidentReport(incidentPostData, timeout);
}
